import type { TokenManager, TokenManagerKeys } from "./types.js";

/**
 * Build a TokenManager for the cookie-based auth model:
 *   - Access token lives in a short-lived HttpOnly cookie (set by the backend).
 *   - The long-lived refresh credential is an HttpOnly **session cookie** the
 *     backend rotates on refresh — JavaScript can neither read nor send it
 *     explicitly (the browser attaches it automatically via withCredentials).
 *
 * Because JS can't read the session cookie, we keep a non-sensitive "1" marker
 * under `refreshTokenKey` so the client knows when a refresh is worth
 * attempting. No token material is ever stored in localStorage.
 */
export function createLocalStorageTokenManager(
	keys: TokenManagerKeys,
): TokenManager {
	return {
		getAccessToken() {
			// Access token lives in an HttpOnly cookie — JS cannot read it.
			return null;
		},
		// Reports whether this device has a session (the marker), NOT a token.
		getRefreshToken() {
			return localStorage.getItem(keys.refreshTokenKey);
		},
		setTokens() {
			// Tokens live in HttpOnly cookies; record only that a session exists.
			localStorage.setItem(keys.refreshTokenKey, "1");
		},
		clearTokens() {
			localStorage.removeItem(keys.accessTokenKey);
			localStorage.removeItem(keys.refreshTokenKey);
		},
	};
}
